// import individual pieces from libraries so that the whole library is not eagerly loaded for each page 
import { Grid, Typography, Button, Box, Tab, Tabs, Dialog, DialogTitle, DialogContent, DialogActions, DialogContentText, Checkbox, TextField as MTextField, Paper } from '@material-ui/core'
import React, { useState, useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom'
import { SiteResponseDTO, SitesClient, OrganizationsClient, UsersClient, UserWithOrganizationResponseDTO, ModemWithOrgSiteResponseDTO } from '../generated/ApiClient'
import { ToTableData, modemTableColumns } from '../components/Modems'
import MUIDataTable from 'mui-datatables'
import { Autocomplete } from '@material-ui/lab';
import { CheckBox, CheckBoxOutlineBlankOutlined } from '@material-ui/icons';
import { SiteDetailsForm } from './SiteForm';
import TabPanel from '../common/TabPanel'
import { useIsUserInRoles, OrgSiteModemEditor } from '../hooks/useIsUserInRoles';
import { tableOptions as options } from '../common/Shared'
import CenteredProgress from '../common/CenteredLoader';

const icon = <CheckBoxOutlineBlankOutlined fontSize="small" />;
const checkedIcon = <CheckBox fontSize="small" />;

const fetchSiteModems = async (id: string, setSiteModems: React.Dispatch<React.SetStateAction<ModemWithOrgSiteResponseDTO[]>>) => {
    const fetchedModems = await new SitesClient().getAllModems(id);
    setSiteModems(fetchedModems);
}

export default function SiteDetail(props: { match: { params: { siteId: string; }; }; }) {
    const siteId = props.match.params.siteId;
    const [siteData, setSiteData] = useState<SiteResponseDTO | 'Loading' | undefined>('Loading');
    const [siteModems, setSiteModems] = useState<ModemWithOrgSiteResponseDTO[]>([]);
    const [availableModems, setAvailableModems] = useState<ModemWithOrgSiteResponseDTO[]>([]);
    const [addModemDialogOpen, setAddModemDialogOpen] = useState(false);
    const [selectedModems, setSelectedModems] = useState<ModemWithOrgSiteResponseDTO[]>([]);
    const [tabIndex, setTabIndex] = useState(0);
    const canEditSites = useIsUserInRoles(OrgSiteModemEditor)
    const handleTabChange = (event: React.ChangeEvent<{}>, newValue: number) => {
        setTabIndex(newValue);
    };

    // Get Site Data
    useEffect(() => {
        const fetchData = async (id: string) => {
            try {
                const fetchedSite = await new SitesClient().get4(id);
                setSiteData(fetchedSite);
            }
            catch (ex) {
                setSiteData(undefined)
            }
        }
        fetchData(siteId);
    }, [siteId, setSiteData]);

    // Get Modems for Site
    useEffect(() => {
        fetchSiteModems(siteId, setSiteModems);
    }, [siteId, setSiteData]);

    // Get Modems available to add
    useEffect(() => {
        const fetchData = async (id: string) => {
            const fetchedModems = await new OrganizationsClient().getAllModemsAll(id);
            setAvailableModems(fetchedModems)
        }
        if (siteData && siteData !== 'Loading') {
            fetchData(siteData.organizationId);
        }
    }, [siteData]);

    function renderSiteAbout(siteData: SiteResponseDTO) {
        return (
            <SiteDetailsForm siteData={siteData} siteId={siteId} />
        );
    }

    function renderSiteModems(siteModems: ModemWithOrgSiteResponseDTO[], site: SiteResponseDTO) {
        return (
            <>
                {canEditSites &&
                    <Grid container item direction='column' spacing={2} alignItems='flex-end'>
                        <Box my={2}>
                            <Button onClick={openDialog} variant="contained" color="primary">Add Modem</Button>
                        </Box>
                    </Grid>}
                <Dialog open={addModemDialogOpen} onClose={closeDialog}>
                    <DialogTitle id="form-dialog-title">Add Modems</DialogTitle>
                    <DialogContent>
                        <DialogContentText>
                            Select modem(s) to assign to {site.name}.
                        </DialogContentText>
                        <Autocomplete
                            multiple
                            id='addModemSelect'
                            options={availableModems.filter(s => siteModems.find(sm => sm.modemHostId === s.modemHostId) === undefined)}
                            value={selectedModems}
                            onChange={(event: any, newValue: ModemWithOrgSiteResponseDTO[]) => setSelectedModems(newValue)}
                            disableCloseOnSelect
                            getOptionLabel={(option) => option.label != '' ? option.label : option.modemHostId}
                            renderOption={(option, { selected }) => (
                                <React.Fragment>
                                    <Checkbox
                                        icon={icon}
                                        checkedIcon={checkedIcon}
                                        style={{ marginRight: 8 }}
                                        checked={selected}
                                    />
                                    {option.label}
                                </React.Fragment>
                            )}
                            style={{ width: 500 }}
                            renderInput={(params) => (
                                <MTextField {...params} variant="outlined" label="Modems" placeholder="Modems" />
                            )}
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={dialogModemsAddedHandler} variant="contained" color="primary">
                            Add
                        </Button>
                    </DialogActions>
                </Dialog>
                <MUIDataTable title={""} data={siteModems.map(sm => ToTableData(sm, [site]))} columns={modemTableColumns(siteModems)} options={tableOptions}></MUIDataTable>
            </>
        )
    }
    const openDialog = () => {
        setAddModemDialogOpen(true);
    }
    const closeDialog = () => {
        setSelectedModems([])
        setAddModemDialogOpen(false);
    }
    const dialogModemsAddedHandler = () => {
        addModems(selectedModems.map((modem) => modem));
        setAddModemDialogOpen(false);
    }
    async function addModems(modems: ModemWithOrgSiteResponseDTO[]) {
        setSelectedModems([]);

        const addModem = async (modem: ModemWithOrgSiteResponseDTO) => {
            if (modem.siteId != undefined)
                await new SitesClient().removeModem2(modem.siteId, modem.modemHostId);
            await new SitesClient().addModem2(siteId, modem.modemHostId);
        }
        await Promise.all(modems.map((modem) => addModem(modem)))
        await fetchSiteModems(siteId, setSiteModems)
    }

    async function removeModems(modemIds: string[]) {
        const removeModem = async (siteId: string, modemId: string) => {
            await new SitesClient().removeModem2(siteId, modemId);
        }

        await Promise.all(modemIds.map((modemId) => removeModem(siteId, modemId)))
        await fetchSiteModems(siteId, setSiteModems)
    }

    const tableOptions = {
        ...options,
        delete: !canEditSites,
        selectableRowsHideCheckboxes: !canEditSites,
        onRowsDelete: ((rowsDeleted: { lookup: any; data: { index: number; dataIndex: number; }[]; }, data: ModemWithOrgSiteResponseDTO[]) => {
            const selectedIndexes = rowsDeleted.data.map((row) => row.dataIndex);
            const modemIds: string[] = selectedIndexes.map((index) => siteModems[index].modemHostId);
            removeModems(modemIds);
        })
    }
    if (siteData === 'Loading')
        return <CenteredProgress />
    else if (siteData)
        return (
            <>
                <Typography variant='h4' id="tabelLabel">{siteData.name}</Typography>
                <Paper square>
                    <Tabs value={tabIndex} onChange={handleTabChange} variant="scrollable" scrollButtons="auto">
                        <Tab label="About" />
                        <Tab label="Modems" />
                    </Tabs>
                </Paper>
                <TabPanel value={tabIndex} index={0}>
                    {renderSiteAbout(siteData)}
                </TabPanel>
                <TabPanel value={tabIndex} index={1}>
                    {renderSiteModems(siteModems, siteData)}
                </TabPanel>
            </>
        );
    else
        return <Typography>Site {siteId} was not found or you do not have access</Typography>
}